import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo, useState } from 'react';
import { useProfileV2 } from '#lib/Profile';
import { getEnvironment } from '#lib/utils/environment';

import { type MenuService, useServices } from '../Services/useServices';

import { SiteBanner } from '../SiteBanner/SiteBanner';
import { UserMenu } from './UserMenu';
import LogoIcon from './LogoIcon';
import MenuLinks from './MenuLinks';
import { MobileMenu } from './MobileMenu';
import { MobileSubMenu } from './MobileSubMenu';
import {
  Nav,
  MenuWrapper,
  SubNav,
  NavWrapper,
  SubNavWrapper,
  IconsWrapper,
  LogoWrapper,
  WatermarkWrapper,
} from './styles';
import SubMenu from './SubMenu';
import UserIconButton from './UserIconButton';
import { type SubButtonProps, type SubLinkProps, type TextType } from './types';
import { NotificationPilotInfo } from './NotificationPilotInfo';

type HeaderMenuProps = {
  /** The name of the service implementing the Menu. It's used to highlight the correct active link in the menu */
  activeServiceName?: string;
  /** Array of objects used to render subMenu links for navigation in your application.*/
  subLinks?: SubLinkProps[];

  subButton?: SubButtonProps;

  //** The translated text that the menu need. The package exports the translation files needed but it's upp to your application to use something like i18n. */
  text: TextType;

  //** Translation object for all the services */
  serviceNames?: Record<string, string>;
};

export const HeaderMenu = ({
  activeServiceName,
  subLinks = [],
  subButton,
  text,
  serviceNames,
}: HeaderMenuProps) => {
  const { basicProfile, profile } = useProfileV2();
  const { isAuthenticated, logout, loginWithRedirect } = useAuth0();
  const [showMenu, setShowMenu] = useState(false);
  const [showServiceLinks, setShowServiceLinks] = useState(false);
  const { services } = useServices();
  const [filteredServices, setFilteredServices] = useState<MenuService[]>([]);
  const isSuperuser = profile?.permissions?.superUser;

  useEffect(() => {
    const filtered = services.filter((s) => s.showInTopMenu);

    setFilteredServices(filtered);
    setShowServiceLinks(true);
  }, [services]);

  const handleUserButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    //This is needed because in React18 addEventListener('click') gets triggered in DropDownMenu
    e.stopPropagation();
    if (!isAuthenticated) {
      await loginWithRedirect();
    }
    setShowMenu((prevState) => !prevState);
  };

  const closeUserMenu = () => {
    setShowMenu(false);
  };

  const watermark = useMemo(() => {
    const environment = getEnvironment();

    const showWatermarkForNBPPilot = () => {
      const now = new Date();
      const NBPPilotLaunchDate = new Date('2025-05-02T12:00:00');

      return now < NBPPilotLaunchDate && isSuperuser && environment === 'production';
    };

    return {
      show:
        environment === 'local' ||
        environment === 'development' ||
        environment === 'test' ||
        showWatermarkForNBPPilot(),
      text: environment.toLocaleUpperCase(),
    };
  }, [isSuperuser]);

  return (
    <>
      {watermark.show && <WatermarkWrapper id="nav-watermark">{watermark.text}</WatermarkWrapper>}
      <SiteBanner scope="global" />
      <MenuWrapper>
        <NavWrapper>
          <Nav>
            <LogoWrapper data-testid="header-logo-wrapper">
              <MobileMenu
                profile={basicProfile}
                activeLink={activeServiceName}
                allServices={filteredServices}
                serviceNames={serviceNames}
                text={text}
              />
              <LogoIcon />
            </LogoWrapper>
            {showServiceLinks && (
              <>
                <MenuLinks
                  activeLink={activeServiceName ?? undefined}
                  profile={basicProfile}
                  allServices={filteredServices}
                  text={text}
                  serviceNames={serviceNames}
                />
                <IconsWrapper>
                  {basicProfile && (
                    <>
                      <UserIconButton onClick={handleUserButtonClick} data-testid="profile-menu" />
                      <NotificationPilotInfo userMenuOpen={showMenu} />
                    </>
                  )}
                  {showMenu && isAuthenticated && (
                    <UserMenu
                      text={text}
                      closeMenu={closeUserMenu}
                      logout={async () => {
                        await logout({ logoutParams: { returnTo: window.location.origin } });
                      }}
                    />
                  )}
                </IconsWrapper>
              </>
            )}
          </Nav>
        </NavWrapper>

        {subLinks.length > 0 && isAuthenticated && (
          <>
            <SubNavWrapper>
              <SubNav>
                <SubMenu subLinks={subLinks} subButton={subButton} />
              </SubNav>
            </SubNavWrapper>
            <MobileSubMenu subLinks={subLinks} subButton={subButton} />
          </>
        )}
      </MenuWrapper>
    </>
  );
};
